import axios from 'axios'
// Split from the value import and marked `type`: every one of these is used
// only in a type position, so an erasing compiler (or Node's type stripping,
// which the refresh regression test uses to load this module) must not emit a
// runtime binding for them — axios exports no such values.
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios'
import toast from 'react-hot-toast'

// `import.meta.env` is injected by Vite. The optional chain is not defensive
// about Vite — it is what lets this module be imported by a plain Node test
// runner (`import.meta.env` is `undefined` outside a bundler, and the property
// read would otherwise throw at module load).
const API_BASE_URL = import.meta.env?.VITE_API_URL || '/api'

/** The one endpoint the 401 interceptor must never try to refresh through. */
const REFRESH_PATH = '/auth/refresh'

/** RFC 9457 Problem Details, as returned by every nu-core service. */
export interface ApiError {
  type: string
  title: string
  status: number
  detail: string
  errors?: unknown[]
}

/** A request the response interceptor has already retried once after a refresh. */
type RetriedRequest = InternalAxiosRequestConfig & { _retry?: boolean }

/** JSON request bodies / query params — opaque to the client, shaped by callers. */
type RequestPayload = Record<string, unknown>

/**
 * Pull the `detail` out of an RFC 9457 error body without asserting `any`.
 * Every catch/onError site in the app wants this exact string.
 */
export function apiErrorDetail(error: unknown, fallback: string): string {
  const detail = apiErrorBody(error)?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

/**
 * The RFC 9457 body of a failed request, if the failure came from the API at
 * all (network errors and thrown non-Axios values yield `undefined`).
 * Callers that need more than `detail` — e.g. the `mfa-required` problem type
 * — narrow from here instead of typing the caught value as `any`.
 */
export function apiErrorBody(
  error: unknown
): (Partial<ApiError> & { userId?: string }) | undefined {
  return (error as AxiosError<ApiError & { userId?: string }> | undefined)?.response?.data
}

/** What the refresh flow needs out of a `POST /v1/auth/refresh` response. */
export interface RefreshedTokens {
  accessToken: string
  /**
   * Present only if the server ROTATED the refresh token. `undefined` means
   * "keep the one already stored" — it does NOT mean "clear it".
   */
  refreshToken?: string
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined
}

/**
 * Read the tokens out of a `POST /v1/auth/refresh` response body (#553).
 *
 * The server returns them TOP-LEVEL and returns no refresh token:
 *
 *   services/management/src/routes/auth.ts  ->  { accessToken, expiresIn }
 *
 * This used to be a bare `const { accessToken, refreshToken } =
 * response.data.tokens`. `response.data.tokens` is always `undefined` on this
 * endpoint, so the destructure threw `TypeError` on EVERY refresh — silent
 * refresh had never once succeeded, and the 401 interceptor's failure path
 * (clear tokens, redirect to /auth/login) ran instead. With a 15-minute access
 * token that is a forced re-login every 15 minutes and a 7-day refresh token
 * that buys nothing.
 *
 * Two things this deliberately does NOT do:
 *
 *  - It does not assume `tokens` is absent. `/auth/login` and `/auth/register`
 *    DO nest under `tokens`, so accepting either shape costs one `??` and
 *    removes a whole class of "which endpoint was this again" breakage.
 *  - It does not return `refreshToken: undefined` as a VALUE to store. The
 *    naive fix — `setTokens(accessToken, newRefreshToken)` with
 *    `newRefreshToken === undefined` — writes the string "undefined" into
 *    localStorage and produces a *different* forced logout on the next refresh.
 *    Absent means "unchanged"; see {@link ApiClient.setTokens}.
 *
 * Whether the server SHOULD rotate the refresh token here is a separate
 * question, and a server-side one. This client accepts a rotation if it ever
 * starts sending one.
 *
 * @throws if the body carries no usable access token — the caller must treat
 *   that as a failed refresh rather than storing a junk credential.
 */
export function readRefreshedTokens(data: unknown): RefreshedTokens {
  const body = (data ?? {}) as {
    accessToken?: unknown
    refreshToken?: unknown
    tokens?: { accessToken?: unknown; refreshToken?: unknown } | null
  }
  const nested = (body.tokens ?? {}) as { accessToken?: unknown; refreshToken?: unknown }

  const accessToken = readString(body.accessToken) ?? readString(nested.accessToken)
  if (!accessToken) {
    throw new Error('Refresh response carried no access token')
  }

  const refreshToken = readString(body.refreshToken) ?? readString(nested.refreshToken)
  return refreshToken ? { accessToken, refreshToken } : { accessToken }
}

class ApiClient {
  private client: AxiosInstance
  /**
   * The in-flight refresh, so concurrent 401s share one call.
   *
   * `Promise<string | null>`, not `Promise<string>`: the chain's own `.catch`
   * resolves to `null` for a failed refresh. This used to typecheck only
   * because the old `response.data.tokens` destructure produced `any` and
   * infected the whole chain — the declared type was already wrong, and reading
   * the response properly is what made the compiler say so.
   */
  private refreshPromise: Promise<string | null> | null = null

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor to handle errors and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config as RetriedRequest | undefined

        // Handle 401 errors (unauthorized).
        //
        // The refresh call itself is excluded. `/auth/refresh` answers 401 for
        // an expired or revoked refresh token, and without this guard that 401
        // re-entered `refreshAccessToken()`, found `this.refreshPromise`
        // already set, and returned it — a promise DOWNSTREAM of this very
        // interceptor. The chain then awaited itself and never settled, so a
        // genuinely expired session hung the caller instead of logging out.
        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest.url?.includes(REFRESH_PATH)
        ) {
          // Check if this is already a retry
          if (originalRequest._retry) {
            this.clearTokens()
            window.location.href = '/auth/login'
            return Promise.reject(error)
          }

          originalRequest._retry = true

          try {
            const newToken = await this.refreshAccessToken()
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              return this.client(originalRequest)
            }
          } catch (refreshError) {
            this.clearTokens()
            window.location.href = '/auth/login'
            return Promise.reject(refreshError)
          }
        }

        // Handle other errors
        if (error.response?.data) {
          const apiError = error.response.data
          if (apiError.detail) {
            toast.error(apiError.detail)
          }
        } else if (error.message) {
          toast.error(error.message)
        }

        return Promise.reject(error)
      }
    )
  }

  private getAccessToken(): string | null {
    return localStorage.getItem('accessToken')
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken')
  }

  /**
   * Store an access token, and a refresh token ONLY if one was supplied.
   *
   * `refreshToken` is optional because `/auth/refresh` does not rotate it
   * (#553). Writing an absent value here would stringify `undefined` into
   * localStorage and break the next refresh — the stored refresh token must
   * survive a response that does not mention it.
   */
  private setTokens(accessToken: string, refreshToken?: string) {
    localStorage.setItem('accessToken', accessToken)
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken)
    }
  }

  private clearTokens() {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
  }

  private async refreshAccessToken(): Promise<string | null> {
    // Prevent multiple refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    const refreshToken = this.getRefreshToken()
    if (!refreshToken) {
      return null
    }

    this.refreshPromise = this.client
      .post(REFRESH_PATH, { refreshToken })
      .then((response) => {
        const refreshed = readRefreshedTokens(response.data)
        // `refreshed.refreshToken` is absent unless the server rotated it, and
        // setTokens leaves the stored one alone in that case.
        this.setTokens(refreshed.accessToken, refreshed.refreshToken)
        return refreshed.accessToken
      })
      .catch(() => {
        this.clearTokens()
        return null
      })
      .finally(() => {
        this.refreshPromise = null
      })

    return this.refreshPromise
  }

  // Auth endpoints
  async login(email: string, password: string, mfaToken?: string, tenantSlug?: string) {
    const response = await this.client.post('/auth/login', {
      email,
      password,
      mfaToken,
      tenantSlug,
    })
    
    if (response.data.requiresMfa) {
      return { requiresMfa: true, userId: response.data.userId }
    }
    
    const { user, tokens } = response.data
    this.setTokens(tokens.accessToken, tokens.refreshToken)
    localStorage.setItem('user', JSON.stringify(user))
    return { user, requiresMfa: false }
  }

  // inviteToken is required by the API (#356 — invite-only registration) for
  // every caller except the still-dormant public self-signup path, so it is
  // accepted here as optional and simply forwarded; the server is the single
  // source of truth on whether it's actually required.
  async register(email: string, password: string, name: string, tenantSlug?: string, inviteToken?: string) {
    const response = await this.client.post('/auth/register', {
      email,
      password,
      name,
      tenantSlug,
      inviteToken,
    })
    const { user, tokens } = response.data
    this.setTokens(tokens.accessToken, tokens.refreshToken)
    localStorage.setItem('user', JSON.stringify(user))
    return user
  }

  async logout() {
    try {
      await this.client.post('/auth/logout')
    } finally {
      this.clearTokens()
      window.location.href = '/auth/login'
    }
  }

  // Password reset
  async requestPasswordReset(email: string) {
    return this.client.post('/password-reset/request', { email })
  }

  async resetPassword(token: string, newPassword: string) {
    return this.client.post('/password-reset/confirm', {
      token,
      newPassword,
    })
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.client.post('/password-reset/change', {
      currentPassword,
      newPassword,
    })
  }

  // Email verification
  async verifyEmail(token: string) {
    return this.client.post('/email-verification/confirm', { token })
  }

  async resendVerificationEmail(email?: string) {
    return this.client.post('/email-verification/resend', { email })
  }

  // MFA
  async setupMFA() {
    return this.client.post('/mfa/setup')
  }

  async verifyMFASetup(secret: string, token: string) {
    return this.client.post('/mfa/verify-setup', { secret, token })
  }

  async verifyMFA(token: string) {
    return this.client.post('/mfa/verify', { token })
  }

  async disableMFA(password: string, token?: string, backupCode?: string) {
    return this.client.post('/mfa/disable', {
      password,
      token,
      backupCode,
    })
  }

  async getMFAStatus() {
    return this.client.get('/mfa/status')
  }

  async regenerateBackupCodes() {
    return this.client.post('/mfa/backup-codes/regenerate')
  }

  // Users
  async getUsers(params?: RequestPayload) {
    return this.client.get('/users', { params })
  }

  async getUser(id: string) {
    return this.client.get(`/users/${id}`)
  }

  async createUser(data: RequestPayload) {
    return this.client.post('/users', data)
  }

  async updateUser(id: string, data: RequestPayload) {
    return this.client.put(`/users/${id}`, data)
  }

  async deleteUser(id: string) {
    return this.client.delete(`/users/${id}`)
  }

  // Tenants
  async getTenants(params?: RequestPayload) {
    return this.client.get('/tenants', { params })
  }

  async getTenant(id: string) {
    return this.client.get(`/tenants/${id}`)
  }

  async createTenant(data: RequestPayload) {
    return this.client.post('/tenants', data)
  }

  async updateTenant(id: string, data: RequestPayload) {
    return this.client.put(`/tenants/${id}`, data)
  }

  async deleteTenant(id: string) {
    return this.client.delete(`/tenants/${id}`)
  }

  // API Keys
  async getApiKeys(params?: RequestPayload) {
    return this.client.get('/api-keys', { params })
  }

  async createApiKey(data: RequestPayload) {
    return this.client.post('/api-keys', data)
  }

  async revokeApiKey(id: string) {
    return this.client.post(`/api-keys/${id}/revoke`)
  }

  // Billing
  async getSubscription() {
    return this.client.get('/billing/subscription')
  }

  async getInvoices() {
    return this.client.get('/billing/invoices')
  }

  async updatePaymentMethod(paymentMethodId: string) {
    return this.client.post('/billing/payment-method', { paymentMethodId })
  }

  // Stats
  async getStats() {
    return this.client.get('/stats')
  }

  // Audit logs
  async getAuditLogs(params?: RequestPayload) {
    return this.client.get('/audit-logs', { params })
  }

  // Profile
  async getCurrentUser() {
    return this.client.get('/users/me')
  }

  async updateProfile(data: RequestPayload) {
    return this.client.put('/users/me', data)
  }

  // Generic methods
  get<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return this.client.get<T>(url, config)
  }

  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return this.client.post<T>(url, data, config)
  }

  put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return this.client.put<T>(url, data, config)
  }

  delete<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return this.client.delete<T>(url, config)
  }
}

export const apiClient = new ApiClient()